/**
 * BizConnect360 Calendar recurrence expansion — shared between the server
 * (associate-facing bc360Calendar.list) and the Admin Dashboard's weekly
 * calendar preview, so a recurring base event expands into occurrences, and
 * hidden occurrences get excluded, exactly the same way in both places.
 */

import { TZDate } from "@date-fns/tz";

export function isCalendarTimeZone(value: string): boolean {
  try { new Intl.DateTimeFormat("en-US", { timeZone: value }); return true; }
  catch { return false; }
}

export type Bc360RecurrenceRule = "none" | "daily" | "weekly" | "monthly";

export interface Bc360RecurringEventLike {
  id: number;
  startTimeUtc: number;
  endTimeUtc: number;
  recurrenceRule?: Bc360RecurrenceRule | string | null;
  recurrenceEndDate?: number | null;
  timeZone?: string | null;
}

// Preserves every field of the caller's own event type T (title, description,
// etc.) via Omit + intersection, rather than widening them to `unknown` —
// callers (chat.ts, the admin/associate dashboards) still get real field
// types, not just the handful of fields this module cares about.
export type Bc360ExpandedOccurrence<T extends Bc360RecurringEventLike = Bc360RecurringEventLike> =
  Omit<T, "id" | "startTimeUtc" | "endTimeUtc"> & {
    id: number | string;
    seriesEventId: number;
    occurrenceStartTimeUtc: number;
    isRecurrenceOccurrence: boolean;
    startTimeUtc: number;
    endTimeUtc: number;
  };

/** Composite key identifying one occurrence for exception lookups. */
export function bc360OccurrenceKey(seriesEventId: number, occurrenceStartTimeUtc: number): string {
  return `${seriesEventId}:${occurrenceStartTimeUtc}`;
}

const MAX_OCCURRENCES = 730; // safety cap: ~2 years of daily events

/**
 * Expand one base event into its occurrences within [windowStart, windowEnd].
 *
 * A non-recurring event returns itself, tagged isRecurrenceOccurrence: false.
 * `hiddenKeys`, when given, drops any occurrence whose
 * bc360OccurrenceKey(seriesEventId, occurrenceStartTimeUtc) is a member of
 * the set — the base row itself is never touched, only that one expanded
 * occurrence is left out of the result.
 *
 * Every result carries seriesEventId / occurrenceStartTimeUtc /
 * isRecurrenceOccurrence explicitly — callers must use those fields and
 * never parse `id` (the first occurrence keeps the numeric base id; later
 * ones get a composite string id for React-key uniqueness only).
 */
export function expandBc360Event<T extends Bc360RecurringEventLike>(
  ev: T,
  windowStart: number,
  windowEnd: number,
  hiddenKeys?: Set<string>,
): Bc360ExpandedOccurrence<T>[] {
  const isRecurring = !!ev.recurrenceRule && ev.recurrenceRule !== "none";

  if (!isRecurring) {
    return [{
      ...ev,
      seriesEventId: ev.id,
      occurrenceStartTimeUtc: ev.startTimeUtc,
      isRecurrenceOccurrence: false,
    } as Bc360ExpandedOccurrence<T>];
  }

  const duration = ev.endTimeUtc - ev.startTimeUtc;
  // BC360 source location timezone verified read-only on 2026-11-03.
  // Legacy rows retain their stored instants; recurrence follows the source schedule.
  const timeZone = ev.timeZone || "America/Chicago";
  if (!isCalendarTimeZone(timeZone)) throw new Error("Invalid calendar timezone");
  const seriesEnd = ev.recurrenceEndDate ? Math.min(ev.recurrenceEndDate, windowEnd) : windowEnd;

  const occurrences: Bc360ExpandedOccurrence<T>[] = [];
  let currentStart = ev.startTimeUtc;
  let legacyStart = ev.startTimeUtc;
  let idx = 0;

  while (currentStart <= seriesEnd) {
    if (currentStart + duration >= windowStart) {
      // Retain UTC-keyed exceptions created before the series zone was recorded.
      const hidden = hiddenKeys?.has(bc360OccurrenceKey(ev.id, currentStart)) ||
        hiddenKeys?.has(bc360OccurrenceKey(ev.id, legacyStart));
      if (!hidden) {
        occurrences.push({
          ...ev,
          id: idx === 0 ? ev.id : `${ev.id}-${currentStart}`,
          startTimeUtc: currentStart,
          endTimeUtc: currentStart + duration,
          seriesEventId: ev.id,
          occurrenceStartTimeUtc: currentStart,
          isRecurrenceOccurrence: true,
        } as Bc360ExpandedOccurrence<T>);
      }
    }
    // Anchor daily/weekly series to the original clock time so a spring gap
    // affects only the nonexistent occurrence, not every subsequent meeting.
    const d = new TZDate(ev.recurrenceRule === "monthly" ? currentStart : ev.startTimeUtc, timeZone);
    if      (ev.recurrenceRule === "daily")   d.setDate(d.getDate() + idx + 1);
    else if (ev.recurrenceRule === "weekly")  d.setDate(d.getDate() + 7 * (idx + 1));
    else if (ev.recurrenceRule === "monthly") d.setMonth(d.getMonth() + 1);
    else break; // unknown rule — stop
    currentStart = d.getTime();
    const legacy = new TZDate(legacyStart, "UTC");
    if (ev.recurrenceRule === "daily") legacy.setDate(legacy.getDate() + 1);
    else if (ev.recurrenceRule === "weekly") legacy.setDate(legacy.getDate() + 7);
    else if (ev.recurrenceRule === "monthly") legacy.setMonth(legacy.getMonth() + 1);
    legacyStart = legacy.getTime();
    idx++;
    if (idx > MAX_OCCURRENCES) break;
  }

  return occurrences;
}
